import type { QueryState } from '@tanstack/query-core';
import { QueryClient } from '@tanstack/react-query';

const MAX_QUERY_RETRIES = 3;
const RETRY_DELAY_BASE_MS = 1000;
const MAX_RETRY_DELAY_MS = 30 * 1000;
const OUTAGE_POLLING_BACKOFF_BASE_MS = 60 * 1000;
const MAX_OUTAGE_POLLING_BACKOFF_MS = 5 * 60 * 1000;

type QueryWithErrorStateWrapper = {
  state: Pick<QueryState<unknown>, 'error' | 'fetchFailureCount'>;
};

export const getHttpResponseStatus = (error: unknown): number | undefined => {
  const status = (error as { response?: { status?: unknown } } | null)?.response
    ?.status;
  return typeof status === 'number' && Number.isInteger(status)
    ? status
    : undefined;
};

export const isHttpServerErrorResponse = (error: unknown): boolean => {
  const status = getHttpResponseStatus(error);

  return status !== undefined && status >= 500 && status < 600;
};

export const shouldRetryUnlessHttpServerError = (
  failureCount: number,
  error: unknown
): boolean =>
  !isHttpServerErrorResponse(error) && failureCount < MAX_QUERY_RETRIES;

export const getDefaultQueryRetryDelay = (failureCount: number): number =>
  Math.min(RETRY_DELAY_BASE_MS * 2 ** failureCount, MAX_RETRY_DELAY_MS);

const getOutageBackoffMs = (failureCount: number): number => {
  const exponent = Math.max(failureCount - 1, 0);
  return Math.min(
    OUTAGE_POLLING_BACKOFF_BASE_MS * 2 ** exponent,
    MAX_OUTAGE_POLLING_BACKOFF_MS
  );
};

export const getHttpServerErrorAwareRefetchInterval = (
  baseInterval: number | false | undefined,
  query: QueryWithErrorStateWrapper
): number | false => {
  if (baseInterval === false || baseInterval === undefined) {
    return false;
  }
  if (!isHttpServerErrorResponse(query.state.error)) {
    return baseInterval;
  }
  return Math.max(
    baseInterval,
    getOutageBackoffMs(query.state.fetchFailureCount)
  );
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: new Date(0).setMinutes(5),
      staleTime: new Date(0).setMinutes(1),
      retry: shouldRetryUnlessHttpServerError,
      retryDelay: getDefaultQueryRetryDelay,
      refetchOnMount: true,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      refetchIntervalInBackground: false
    }
  }
});
